import { Vector3, MeshBuilder, Animation, EasingFunction, CubicEase } from "@babylonjs/core";
import { VFXManager } from "../logic/VFXManager.js";
import { DataCollector } from "../logic/ai/DataCollector.js";
import { InputManager } from "../logic/InputManager.js";
import { LevelManager } from "../logic/LevelManager.js";
import { EntityManager } from "../logic/EntityManager.js";
import { AudioManager } from "../logic/AudioManager.js";
import { Player } from "../entities/Player.js";
import { HUDManager } from "../logic/hud/HUDManager.js";
import { UIManager } from "../logic/UIManager.js";
import { DebugManager } from "../debug/DebugManager.js";
import { eventBus, EVENTS } from "./EventBus.js";

/**
 * @typedef {Object} GameContext
 * @property {import("@babylonjs/core").Scene} scene
 * @property {import("@babylonjs/core").Engine} engine
 * @property {import("@babylonjs/core").FollowCamera} camera
 * @property {GameManager} gameManager
 * @property {import("../logic/ai/DataCollector.js").DataCollector} ai
 * @property {import("../logic/InputManager.js").InputManager} inputs
 * @property {import("../logic/AudioManager.js").AudioManager} audioManager
 * @property {import("../logic/EntityManager.js").EntityManager} entityManager
 * @property {import("../entities/Player.js").Player} player
 * @property {import("../logic/hud/HUDManager.js").HUDManager} hudManager
 * @property {import("../logic/UIManager.js").UIManager} uiManager
 * @property {import("../logic/LevelManager.js").LevelManager} levelManager
 * @property {import("../debug/DebugManager.js").DebugManager} debugManager
 */

/**
 * @class GameManager
 * @description Orchestrateur principal du jeu. Gère les états, les managers et la boucle principale.
 */
export class GameManager {
    /**
     * @param {import("@babylonjs/core").Scene} scene
     * @param {import("@babylonjs/core").Engine} engine
     * @param {import("@babylonjs/core").FollowCamera} camera
     */
    constructor(scene, engine, camera) {
        /** @type {GameContext} */
        this.context = {
            scene,
            engine,
            camera,
            gameManager: this
        };

        /** @enum {string} */
        this.STATES = {
            START: "START",
            PLAYING: "PLAYING",
            PAUSED: "PAUSED",
            GAMEOVER: "GAMEOVER",
            GAMEWON: "GAMEWON"
        };

        /** @type {string} */
        this.gameState = this.STATES.START;

        /** @type {number} */
        this.gameStartTime = 0;

        /** @type {import("@babylonjs/core").Mesh|null} */
        this.cameraTarget = null;

        // 2. Initialiser les managers via le contexte
        this._initManagers();
        this._subscribeToEvents();
        this._startGameLoop();

        // Sécurisation : Exposition globale uniquement en mode debug
        const DEBUG = new URLSearchParams(window.location.search).has('debug'); // Configurabilité via URL param
        if (DEBUG) {
            window.game = this;
        }
    }

    /**
     * Initialise tous les sous-systèmes du jeu
     * @private
     */
    _initManagers() {
        // Création séquentielle et ajout au contexte
        this.context.ai = new DataCollector();
        this.context.inputs = new InputManager(this.context);

        this.context.audioManager = new AudioManager(this.context.scene, this.context.engine);

        this.context.entityManager = new EntityManager(this.context);
        this.context.player = new Player(this.context);

        this.context.hudManager = new HUDManager();
        this.context.uiManager = new UIManager(this.context.hudManager);

        this.context.levelManager = new LevelManager(this.context, {
            onLevelLoaded: this._onLevelLoaded.bind(this),
            onRoomCleared: this._onRoomCleared.bind(this),
            onGameWon: this._onGameWon.bind(this)
        });

        this.context.vfx = new VFXManager(this.context.scene, this.context.camera);

        this.context.debugManager = new DebugManager(this.context);
        this.context.debugManager.init();

        // Rendre les managers accessibles directement sur le GameManager (pour compatibilité)
        Object.assign(this, this.context);

        this.uiManager.showStartScreen();
    }

    /**
     * S'abonne aux événements globaux via l'EventBus
     * @private
     */
    _subscribeToEvents() {
        eventBus.on(EVENTS.GAME_START, () => this.startGame());

        eventBus.on("RESUME_REQUESTED", () => {
            if (this.gameState === this.STATES.PAUSED) {
                this.togglePause();
            }
        });

        eventBus.on("VOLUME_CHANGED", (vol) => {
            this.audioManager.setMasterVolume(vol);
        });
        eventBus.on("LAYOUT_CHANGED", (layout) => {
            this.inputs.setLayout(layout);
        });

        eventBus.on("ENTER_PRESSED", async () => {
            await this.audioManager.unlockAudio();
            if (this.gameState === this.STATES.START || this.gameState === this.STATES.GAMEOVER || this.gameState === this.STATES.GAMEWON) {
                eventBus.emit(EVENTS.GAME_START);
            }
        });
    }

    /**
     * Démarre une nouvelle partie
     * @async
     */
    async startGame() {
        await this.audioManager.unlockAudio();

        const selectedLayout = document.querySelector('input[name="layout"]:checked')?.value || "WASD";
        this.inputs.setLayout(selectedLayout);

        this.gameState = this.STATES.PLAYING;

        this.ai.reset();
        this.gameStartTime = Date.now();
        this.player.reset();
        this.entityManager.clearAll();

        this.audioManager.playMusic("ambient");

        this.levelManager.setCameraSnap((spawnPos) => this._snapCameraToPlayer(spawnPos));
        if (!this.cameraTarget) {
            this.cameraTarget = MeshBuilder.CreateBox("cameraTarget", { size: 0.1 }, this.scene);
            this.cameraTarget.isVisible = false;
        }
        this.camera.lockedTarget = this.cameraTarget;

        this.levelManager.loadFloor(1, this.player, this.ai);
    }

    /**
     * Redémarre la partie
     */
    restartGame() {
        this.startGame();
    }

    /**
     * Alterne entre l'état PLAYING et PAUSED
     */
    togglePause() {
        if (this.gameState === this.STATES.PLAYING) {
            this.gameState = this.STATES.PAUSED;
            eventBus.emit(EVENTS.GAME_PAUSE);

            const currentLayout = this.inputs.layout;
            const radio = document.querySelector(`input[name="pause-layout"][value="${currentLayout}"]`);
            if (radio) radio.checked = true;

        } else if (this.gameState === this.STATES.PAUSED) {
            this.gameState = this.STATES.PLAYING;
            eventBus.emit(EVENTS.GAME_RESUME);
        }
    }

    /**
     * Callback appelé quand un étage est chargé
     * @param {import("@babylonjs/core").Vector3[]} spawnPoints
     * @param {string[]} enemyTypes
     * @private
     */
    _onLevelLoaded(spawnPoints, enemyTypes) {
        this.entityManager.clearAll();

        if (this.debugManager && !this.debugManager.shouldSpawnEnemies()) {
            return;
        }

        const types = (enemyTypes && Array.isArray(enemyTypes)) ? enemyTypes : ["Traqueur"];
        const count = Math.min(spawnPoints.length, types.length);
        for (let i = 0; i < count; i++) {
            this.entityManager.spawnEnemy(types[i], spawnPoints[i]);
        }

        if (this.levelManager.currentFloor === 5) {
            this.audioManager.playMusic("boss");
        } else if (this.audioManager.currentMusicKey === "boss") {
            this.audioManager.playMusic("ambient");
        }
    }

    /**
     * Callback appelé quand une salle est nettoyée
     * @param {number} roomIndex
     * @private
     */
    _onRoomCleared(roomIndex) {
        eventBus.emit(EVENTS.ROOM_CLEARED, roomIndex);

        // Récompense de fin d'étage : augmentation des HP max
        // Déclenché uniquement sur la dernière salle (portail rouge / changement d'étage)
        const config = this.levelManager?.currentFloorConfig;
        const floorNumber = this.levelManager?.currentFloor ?? 0;
        // +4 HP max sur la dernière salle de chaque étage normal (pas le boss)
        if (config && roomIndex === config.rooms - 1 && floorNumber < 4) {
            this.player.increaseMaxHealth(4);
        }
    }

    /**
     * Callback appelé quand le jeu est gagné
     * @private
     */
    _onGameWon() {
        this.gameState = this.STATES.GAMEWON;
        this.audioManager.stopAll();
        this.audioManager.playSound("bonus");

        const time = Math.floor((Date.now() - this.gameStartTime) / 1000);
        const min = Math.floor(time / 60).toString().padStart(2, '0');
        const sec = (time % 60).toString().padStart(2, '0');

        eventBus.emit(EVENTS.GAME_WON, {
            score: `${Math.floor(Math.random() * 20 + 80)}%`,
            time: `${min}:${sec}`
        });
    }

    /**
     * Transitionne vers l'état Game Over
     * @private
     */
    _transitionToGameOver() {
        if (this.gameState === this.STATES.GAMEOVER) return;

        this.gameState = this.STATES.GAMEOVER;
        this.audioManager.stopAll();
        eventBus.emit(EVENTS.GAME_OVER);
    }

    /**
     * Lance la boucle de rendu/update de Babylon.js
     * @private
     */
    _startGameLoop() {
        this.scene.onBeforeRenderObservable.add(() => {
            this._update();
        });
    }

    /**
     * Mise à jour logique à chaque frame
     * @private
     */
    _update() {
        if (this.debugManager) this.debugManager.update();

        if (this.inputs.isPauseTriggered()) {
            if (this.gameState === this.STATES.PLAYING || this.gameState === this.STATES.PAUSED) {
                this.togglePause();
            }
        }

        if (this.gameState !== this.STATES.PLAYING) return;

        this.vfx.update();
        this.player.update(this.inputs, this.ai, this.levelManager);
        if (this.cameraTarget && this.player.mesh) {
            this.cameraTarget.position.copyFrom(this.player.mesh.position);
        }

        this.levelManager.checkBonusInteraction(this.player);
        this.levelManager.checkExitInteraction(this.player, this.entityManager, this.ai);

        this._handleCameraZoom();

        const collisionDetected = this.entityManager.update(this.player, this.ai);

        if (collisionDetected && (Date.now() - this.gameStartTime > 1000)) {
            if (!this.debugManager || this.debugManager.shouldTakeDamage()) {
                const damageResult = this.player.takeDamage();
                if (damageResult !== null) {
                    this.ai.recordDamage(1);
                    this.hudManager.applyGlitchEffect();
                }
            }
        }

        if (Date.now() - this.gameStartTime > 1000 && this.player.currentHealth <= 0) {
            this._transitionToGameOver();
        }

        if (this.entityManager.getEnemyCount() === 0) {
            this.levelManager.onRoomEnemiesCleared();
        }

        if (this.ai.shouldAdapt()) {
            this.levelManager.applyGlitchEffect();
            this.entityManager.applyAdaptation(this.ai);
            eventBus.emit(EVENTS.AI_ADAPTATION);
        }

        this._updateHUD();
    }

    /**
     * Gère le zoom caméra via les touches de raccourci
     * @private
     */
    _handleCameraZoom() {
        if (this.inputs.isZoomInTriggered() && this.camera.radius > 8) {
            this.camera.radius *= 0.98;
            this.camera.heightOffset *= 0.98;
        }
        if (this.inputs.isZoomOutTriggered() && this.camera.radius < 40) {
            this.camera.radius *= 1.02;
            this.camera.heightOffset *= 1.02;
        }
    }

    /**
     * Met à jour les données affichées dans le HUD
     * @private
     */
    _updateHUD() {
        const hudData = {
            aiProgress: (this.ai.actionCounter / this.ai.threshold) * 100,
            health: this.player.getHealthData(),
            dash: this.player.getDashData(),
            power: {
                active: this.player.activePower,
                stored: this.player.storedPower,
                timerPercent: this.player.activePower ? (this.player.powerTimer / 600) * 100 : 0
            }
        };

        this.hudManager.update(hudData);
        this.hudManager.updateDebug(this.ai);
    }

    /**
     * Aligne instantanément la caméra sur une position de spawn
     * @param {import("@babylonjs/core").Vector3} spawnPos
     * @private
     */
    _snapCameraToPlayer(spawnPos) {
        if (!this.camera) return;

        // Position finale normale
        this.camera.radius = 16;
        this.camera.rotationOffset = 180;

        if (this.cameraTarget) {
            this.cameraTarget.position.copyFrom(spawnPos);
            this.cameraTarget.rotation = Vector3.Zero();
            this.camera.lockedTarget = this.cameraTarget;
        }

        // Intro cinématique : part de très haut et descend en douceur
        this.camera.heightOffset = 55;
        this._playCameraIntro();
    }

    /**
     * Anime la caméra depuis une vue de dessus jusqu'à la position de jeu normale.
     * @private
     */
    _playCameraIntro() {
        const fps = 60;
        const durationFrames = 110; // ~1.8s

        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);

        const animHeight = new Animation(
            "camIntroHeight", "heightOffset", fps,
            Animation.ANIMATIONTYPE_FLOAT,
            Animation.ANIMATIONLOOPMODE_CONSTANT
        );
        animHeight.setEasingFunction(ease);
        animHeight.setKeys([
            { frame: 0,              value: 55 },
            { frame: durationFrames, value: 10 }
        ]);

        const animRadius = new Animation(
            "camIntroRadius", "radius", fps,
            Animation.ANIMATIONTYPE_FLOAT,
            Animation.ANIMATIONLOOPMODE_CONSTANT
        );
        animRadius.setEasingFunction(ease);
        animRadius.setKeys([
            { frame: 0,              value: 30 },
            { frame: durationFrames, value: 16 }
        ]);

        this.scene.stopAnimation(this.camera);
        this.scene.beginDirectAnimation(
            this.camera,
            [animHeight, animRadius],
            0, durationFrames,
            false, 1
        );
    }
}